import * as React from "react";
import { AlertColor, Stack, TextField, TextFieldProps } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import CclDrawer from "../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../components/common/cclDrawer/cclDrawerActionBar";
import CclButton from "../../../components/common/cclButtons/cclButton";
import CclLoadingButton from "../../../components/common/cclButtons/cclLoadingButton";
import { useAddPayDateMutation } from "../../../services/cclTokenedPayrollApi";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";

export interface AddPayDateDrawerProps {
    open: boolean;
    onClose: () => void;
    onSaveResult: (message: string, severity: AlertColor) => void;
}

const toLocalDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const AddPayDateDrawer: React.FC<AddPayDateDrawerProps> = (props) => {
    const [payDate, setPayDate] = React.useState<Date | null>(null);
    const [addPayDate, { isLoading }] = useAddPayDateMutation();
    const { logEvent } = useLogAccessEvent();

    React.useEffect(() => {
        if (props.open) {
            setPayDate(null);
        }
    }, [props.open]);

    const isValidDate = payDate != null && !Number.isNaN(payDate.getTime());

    const handleSave = () => {
        if (!isValidDate || payDate == null) {
            return;
        }

        addPayDate({ payDays: toLocalDateString(payDate) })
            .unwrap()
            .then(() => {
                logEvent("PayrollPayDateAdded", null);
                props.onSaveResult("Pay Date Added", "success");
                props.onClose();
            })
            .catch((error) => {
                console.error("Error adding pay date:", error);
                props.onSaveResult("Failed to add Pay Date", "error");
            });
    };

    return (
        <CclDrawer title="Add Pay Date" open={props.open} onClose={props.onClose}>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <DatePicker
                    label="Pay Date"
                    value={payDate}
                    onChange={(newValue: Date | null) => setPayDate(newValue)}
                    renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => (
                        <TextField {...params} id="add-pay-date" size="small" fullWidth />
                    )}
                />
                <CclDrawerActionBar>
                    <CclLoadingButton
                        mode="primary"
                        loading={isLoading}
                        disabled={!isValidDate || isLoading}
                        onClick={handleSave}
                    >
                        Save
                    </CclLoadingButton>
                    <CclButton mode="secondary" onClick={props.onClose}>
                        Cancel
                    </CclButton>
                </CclDrawerActionBar>
            </Stack>
        </CclDrawer>
    );
};

export default AddPayDateDrawer;
